// src/utils/pythonHighlight.js
//
// Dependency-free Python tokeniser used to colour the committed code snippet a
// Logic block shows in its Code tab. This is a display aid only: a token says
// which colour to paint and nothing else, so an imperfect match degrades to
// uncoloured text rather than changing what the reader sees.

const KEYWORDS = [
  'False', 'None', 'True', 'and', 'as', 'assert', 'async', 'await', 'break',
  'class', 'continue', 'def', 'del', 'elif', 'else', 'except', 'finally', 'for',
  'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'nonlocal', 'not',
  'or', 'pass', 'raise', 'return', 'try', 'while', 'with', 'yield',
];

const BUILTINS = [
  'abs', 'all', 'any', 'bool', 'dict', 'enumerate', 'filter', 'float', 'format',
  'int', 'isinstance', 'len', 'list', 'map', 'max', 'min', 'open', 'print',
  'range', 'round', 'set', 'sorted', 'str', 'sum', 'tuple', 'type', 'zip',
];

// Ordered alternation: a construct that can contain other constructs — a
// comment or a string — must be matched before the things it might contain,
// otherwise `# not a def` would colour `def` as a keyword.
const TOKEN_PATTERN = new RegExp([
  '(#[^\\n]*)',                                                                   // comment
  '([fFrRbBuU]{0,2}(?:"""[\\s\\S]*?"""|\'\'\'[\\s\\S]*?\'\'\'))',                  // triple-quoted string
  '([fFrRbBuU]{0,2}(?:"(?:\\\\.|[^"\\\\\\n])*"|\'(?:\\\\.|[^\'\\\\\\n])*\'))',     // single-line string
  '(@[A-Za-z_][A-Za-z0-9_]*)',                                                    // decorator
  `\\b(${KEYWORDS.join('|')})\\b`,                                                // keyword
  `\\b(${BUILTINS.join('|')})\\b`,                                                // builtin
  '\\b(\\d[\\d_]*(?:\\.[\\d_]+)?(?:[eE][+-]?\\d+)?)\\b',                          // number
].join('|'), 'g');

// Index in this list matches the capture-group number in TOKEN_PATTERN.
const GROUP_KINDS = [
  null, 'comment', 'string', 'string', 'decorator', 'keyword', 'builtin', 'number',
];

export const PYTHON_TOKEN_COLOURS = {
  plain: 'inherit',
  comment: '#7A8494',
  string: '#C3E88D',
  decorator: '#FFCB6B',
  keyword: '#C792EA',
  builtin: '#82AAFF',
  number: '#F78C6C',
};

// Returns { text, kind } tokens that concatenate back to the exact input, so
// the rendered block is always the committed code verbatim.
export const tokenisePython = (code) => {
  if (!code) return [];

  const tokens = [];
  let cursor = 0;
  let match;

  TOKEN_PATTERN.lastIndex = 0;
  while ((match = TOKEN_PATTERN.exec(code)) !== null) {
    if (match.index > cursor) {
      tokens.push({ text: code.slice(cursor, match.index), kind: 'plain' });
    }
    let kind = 'plain';
    for (let group = 1; group < GROUP_KINDS.length; group += 1) {
      if (match[group] !== undefined) {
        kind = GROUP_KINDS[group];
        break;
      }
    }
    tokens.push({ text: match[0], kind });
    cursor = match.index + match[0].length;
  }

  if (cursor < code.length) {
    tokens.push({ text: code.slice(cursor), kind: 'plain' });
  }

  return tokens;
};
